// src/components/EquipoTab.jsx
// Conecta la ficha de un usuario (Nomina) con la jerarquia organizacional:
// muestra el cargo/area actual, el jefe directo y, si tiene jefatura, el
// equipo de personas a su cargo. Reutiliza el mismo endpoint/estructura
// que ya usa la pagina "Mi Equipo", solo que resuelto por email en vez
// del usuario autenticado (uso administrativo desde Nomina).
import React, { useEffect, useState } from 'react';
import { Building2, Star, UserCircle, AlertCircle } from 'lucide-react';
import { Users, FolderTree } from './icons';
import { miEquipoAPI } from '../services/api';

function AreaNode({ area, depth = 0 }) {
  const hasCargos = Array.isArray(area.cargos) && area.cargos.length > 0;
  const hasChildren = Array.isArray(area.children) && area.children.length > 0;

  return (
    <div className={depth > 0 ? 'ml-5 border-l border-gray-200 dark:border-gray-700 pl-4' : ''}>
      <div className="flex items-center gap-2 py-2">
        <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{area.nombre}</span>
        <span className="text-xs text-gray-400">{area.tipoArea}</span>
      </div>

      {hasCargos && (
        <div className="space-y-1.5 mb-2">
          {area.cargos.map((cargo) => (
            <div
              key={cargo.id || `${cargo.usuario?.id}-${cargo.cargo}`}
              className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/40"
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${cargo.esJefatura
                ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white'
                : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}>
                {cargo.esJefatura ? <Star className="w-4 h-4" /> : <UserCircle className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {cargo.usuario?.nombre} {cargo.usuario?.apellido}
                  </span>
                  {cargo.esJefatura && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300">
                      Jefe
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{cargo.cargo}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasChildren && area.children.map((child) => (
        <AreaNode key={child.id} area={child} depth={depth + 1} />
      ))}
    </div>
  );
}

export default function EquipoTab({ email }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!email) {
      setLoading(false);
      setData({ tieneCuenta: false });
      return;
    }
    setLoading(true);
    setError(null);
    miEquipoAPI.getEquipoPorEmail(email)
      .then(({ data: res }) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setError('No se pudo cargar la informacion de jerarquia.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [email]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        {error}
      </div>
    );
  }

  if (!data?.tieneCuenta) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
        <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />
        Este usuario no tiene una cuenta de acceso al sistema, por lo que no hay informacion de cargo ni jerarquia para mostrar.
      </div>
    );
  }

  const cargos = data.usuario?.cargos || [];
  const jefeDirecto = data.jefeDirecto;
  const equipo = data.equipo || [];
  const companeros = data.companeros || [];
  const cadenaJefes = data.cadenaJefes || [];

  return (
    <div className="space-y-6">
      {/* Cargo actual + Jefe directo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            Cargo en el organigrama
          </h3>
          {cargos.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Sin cargo asignado en el organigrama.</p>
          ) : (
            <div className="space-y-2">
              {cargos.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{c.cargo}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">{c.area}</p>
                  </div>
                  {c.esJefatura && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300">
                      Jefatura
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <UserCircle className="w-4 h-4 text-gray-400" />
            Jefe directo
          </h3>
          {jefeDirecto ? (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center font-semibold text-sm">
                {jefeDirecto.nombre?.[0]}{jefeDirecto.apellido?.[0]}
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {jefeDirecto.nombre} {jefeDirecto.apellido}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Sin jefe directo asignado.</p>
          )}
          {cadenaJefes.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
              {cadenaJefes.map((j, i) => (
                <div key={j.id} className="flex items-center gap-3" style={{ marginLeft: `${(i + 1) * 12}px` }}>
                  <div className="w-8 h-8 rounded-full bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 flex items-center justify-center font-semibold text-xs">
                    {j.nombre?.[0]}{j.apellido?.[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{j.nombre} {j.apellido}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {i === 0 ? `Jefe de ${jefeDirecto?.nombre}` : 'Jefatura superior'}{j.cargo ? ` · ${j.cargo}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Compañeros de equipo (misma area) */}
      {companeros.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            Equipo de trabajo
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              {companeros.length} persona{companeros.length === 1 ? '' : 's'}
            </span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {companeros.map((c) => (
              <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/40">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${c.esJefatura
                  ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white'
                  : 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300'}`}>
                  {c.nombre?.[0]}{c.apellido?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{c.nombre} {c.apellido}</span>
                    {c.esJefatura && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300">
                        Jefe
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{c.cargo}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Equipo a cargo (solo si tiene jefatura) */}
      {equipo.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            Equipo a cargo
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              <FolderTree className="w-3 h-3" />
              {data.totalPersonas} persona{data.totalPersonas === 1 ? '' : 's'}
            </span>
          </h3>
          <div className="space-y-1">
            {equipo.map((area) => (
              <AreaNode key={area.id} area={area} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
